import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../services/auth';
import socketService from '../services/socket';

// Auth Context 생성
const AuthContext = createContext(null);

// [2026-10-07 미니게임] 게임종류 목록을 아직 모를 때(불러오기 전·불러오기 실패) 쓰는 값. 여기 없는 종류는 종전대로 노출(true).
//   미니게임은 서버 기본값이 미운영이다(user-api config/gameAccessPolicy.js GAME_TYPE_DEFAULTS — 운영사가 켠 곳만 쓴다).
//   WHY: 모를 때 "전부 노출"로 보면 미니게임을 켜지 않은 모든 사이트에서 상단 메뉴에 미니게임이 잠깐 떴다가 사라진다
//        (접속·새로고침마다 메뉴 줄이 흔들림). 게스트 공개 게임종류 조회가 실패할 때 서버도 같은 이유로 "전부 활성"이 아니라
//        기본값을 준다(user-api routes/userAuth.js public-game-types). 카지노·슬롯·스포츠는 종전과 같이 노출(깜빡임 방지).
const GAME_TYPE_DEFAULT_WHEN_UNKNOWN = Object.freeze({ minigame: false });

// Auth Provider 컴포넌트
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [domainInfo, setDomainInfo] = useState(null);
  // [운영사 게임종류 토글] 운영 중인 게임종류(카지노/슬롯/스포츠/미니게임) effective 목록.
  //   null = 미확정(로딩 전) → 화면은 "전부 노출"로 간주(깜빡임 방지, 미니게임만 숨김 — 위 GAME_TYPE_DEFAULT_WHEN_UNKNOWN). 응답 수신 시 실제 배열로 대체.
  const [enabledGameTypes, setEnabledGameTypes] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  // [2026-10-31] 첫 로그인 상태 확인을 마쳤는가 — 로그인·회원가입 화면으로 처음 들어오면 그 화면을 떠날 때까지 미룬다(아래 효과).
  const authCheckedRef = useRef(false);

  // [2026-10-31] 게스트 게임종류(공개 창구)만 받는다 — 아래 checkAuthStatus 게스트 갈래와 같은 규칙(배열이 아니면 그대로 둔다 = 종전 fail-open).
  const loadGuestGameTypes = async () => {
    try {
      const pub = await authService.getPublicEnabledGameTypes();
      if (Array.isArray(pub?.enabledGameTypes)) {
        setEnabledGameTypes(pub.enabledGameTypes);
      }
    } catch (e) { /* fail-open: null 유지(모를 때의 노출 규칙은 아래 isGameTypeEnabled) */ }
  };

  // 로그인 상태 확인
  //   [2026-10-31] 로그인·회원가입 화면을 주소로 바로 열면(새 탭·즐겨찾기·그 화면에서 새로고침) 종전에는 확인을 통째로 건너뛰고 다시 하지
  //   않아, 그 화면을 떠나도 ① 로그인한 회원이 로그아웃된 것처럼 보이고("상담 채팅"을 누르면 로그인 화면으로) ② 비로그인 방문자는 운영사
  //   게임종류가 메뉴에 맞게 안 보였다(미니게임 메뉴 사라짐 — 2026-10-31 ur3 실측 1440·390). 이제 그 화면에서는 게스트 게임종류만 받고,
  //   로그인하지 않은 채 다른 화면으로 옮길 때 확인을 한 번 한다(로그인하면 login() 이 상태를 정한다).
  //   WHY(그 화면에서 곧바로 확인하지 않는 이유 — 종전 원칙 유지): 확인이 성공하면 실시간 연결을 여는데, 같은 화면에서 다른 계정으로
  //   로그인하면 services/socket.js connect() 가 '이미 연결됨'으로 돌아가 옛 계정의 연결이 남는다.
  useEffect(() => {
    if (authCheckedRef.current) return;
    // 로그인/회원가입 페이지에서는 인증 상태 확인 및 소켓 연결 방지
    if (location.pathname === '/login' || location.pathname === '/register') {
      loadGuestGameTypes();
      setLoading(false);
      return;
    }
    authCheckedRef.current = true;
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // 회원레벨 변경 이벤트 감지 - 관리자가 레벨 변경 시 상태 갱신
  useEffect(() => {
    const handleMemberLevelChanged = () => {
      console.log('useAuth: 회원레벨 변경 감지, 인증 상태 갱신');
      checkAuthStatus();
    };

    window.addEventListener('memberLevelChanged', handleMemberLevelChanged);
    return () => {
      window.removeEventListener('memberLevelChanged', handleMemberLevelChanged);
    };
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // [게스트 게임종류] 토큰 없음(게스트)일 때도 도메인 기준 운영사 활성 게임종류를
        //   공개 엔드포인트로 받아 enabledGameTypes 에 채운다. WHY: 안 채우면 null 로 남아
        //   isGameTypeEnabled 가 전부 true → 운영사가 끈 스포츠가 미로그인 화면에 노출된다.
        //   배열이 아니면(실패/형식이상) set 생략 → null 유지 = 기존 fail-open(전부 노출).
        try {
          const pub = await authService.getPublicEnabledGameTypes();
          if (Array.isArray(pub?.enabledGameTypes)) {
            setEnabledGameTypes(pub.enabledGameTypes);
          }
        } catch (e) { /* fail-open: null 유지(전부 노출) */ }
        setLoading(false);
        return;
      }

      const data = await authService.checkAuthStatus();
      if (data.authenticated) {
        setUser(data.user);
        // userId localStorage에 저장
        if (data.user && data.user.id) {
          localStorage.setItem('userId', data.user.id);
        }
        // [운영사 게임종류 토글] effective 게임종류 저장 (배열 아니면 null 유지=전부 노출)
        setEnabledGameTypes(Array.isArray(data.enabledGameTypes) ? data.enabledGameTypes : null);
        // 도메인 정보 저장
        if (data.domainInfo) {
          setDomainInfo(data.domainInfo);
        }
        // 웹소켓 연결
        socketService.connect();
      } else {
        localStorage.removeItem('token');
        // 로그인 페이지로 리디렉션 (로그인 페이지가 아닌 경우만)
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          // navigate('/login');  // 주석 처리 - 비로그인 사용자도 홈페이지 접근 가능하도록
        }
      }
    } catch (error) {
      console.error('인증 상태 확인 실패:', error);
      localStorage.removeItem('token');
      // JWT 만료 오류 처리
      if (error.response?.status === 401 || error.message?.includes('jwt expired')) {
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          // navigate('/login');  // 주석 처리 - 비로그인 사용자도 홈페이지 접근 가능하도록
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      // 로그인 전 소켓 플래그 리셋
      socketService.resetFlags();
      
      const data = await authService.login(credentials);
      
      if (data.success) {
        console.log('useAuth: 로그인 성공');
        setUser(data.user);
        authCheckedRef.current = true;   // [2026-10-31] 로그인으로 상태가 정해졌다 — 로그인 화면을 떠날 때 다시 확인하지 않는다(위 효과)
        // userId localStorage에 저장
        if (data.user && data.user.id) {
          localStorage.setItem('userId', data.user.id);
        }
        // [운영사 게임종류 토글] 로그인 직후에도 탭 노출이 정확하도록 effective 게임종류 저장
        setEnabledGameTypes(Array.isArray(data.enabledGameTypes) ? data.enabledGameTypes : null);

        // 할당된 도메인 확인 및 리디렉션 (현재 도메인과 다르면 할당 도메인으로 이동)
        if (data.assignedDomain && data.assignedDomain.url) {
          const currentHostname = window.location.hostname;
          const assignedUrl = data.assignedDomain.url;

          let assignedHostname;
          try {
            assignedHostname = new URL(assignedUrl.startsWith('http') ? assignedUrl : `https://${assignedUrl}`).hostname;
          } catch (e) {
            assignedHostname = assignedUrl;
          }

          if (currentHostname !== assignedHostname) {
            console.log(`useAuth: 할당된 도메인으로 리디렉션 - 현재: ${currentHostname}, 할당: ${assignedHostname}`);
            const redirectUrl = assignedUrl.startsWith('http') ? assignedUrl : `https://${assignedUrl}`;
            window.location.href = redirectUrl;
            return { success: true, redirecting: true };
          }
        }

        // 웹소켓 연결
        socketService.connect();
        
        // 팝업 매니저에게 인증 상태 변경 알림
        console.log('useAuth: 로그인 후 인증 상태 변경 이벤트 발생');
        window.dispatchEvent(new CustomEvent('authStateChanged'));
        
        // storage 이벤트 수동 발생
        setTimeout(() => {
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'token',
            oldValue: null,
            newValue: localStorage.getItem('token'),
            storageArea: localStorage
          }));
        }, 100);
        
        return { success: true };
      }
      
      return { success: false, error: data.error || '로그인에 실패했습니다.' };
    } catch (error) {
      console.error('로그인 실패:', error);
      return { success: false, error: error.error || error.message || '로그인 중 오류가 발생했습니다.' };
    }
  };

  const logout = async () => {
    try {
      console.log('useAuth: 로그아웃 시작');
      await authService.logout();
      socketService.disconnect();
      setUser(null);
      // [운영사 게임종류 토글] 로그아웃 시 게임종류 상태 초기화
      setEnabledGameTypes(null);
      // [게스트 게임종류] 로그아웃 직후에도 운영사가 끈 게임종류(예: 스포츠)가 다시 노출되지
      //   않도록 게스트용 공개 게임종류를 재조회해 반영. 실패 시 null 유지(전부 노출, fail-open).
      try {
        const pub = await authService.getPublicEnabledGameTypes();
        if (Array.isArray(pub?.enabledGameTypes)) {
          setEnabledGameTypes(pub.enabledGameTypes);
        }
      } catch (e) { /* fail-open */ }

      // 팝업 매니저에게 인증 상태 변경 알림
      console.log('useAuth: 인증 상태 변경 이벤트 발생');
      window.dispatchEvent(new CustomEvent('authStateChanged'));
      
      // storage 이벤트는 같은 창에서는 발생하지 않으므로 수동으로 처리
      // 약간의 지연을 두고 실행하여 localStorage 변경이 완료된 후 실행되도록 함
      setTimeout(() => {
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'token',
          oldValue: null,
          newValue: null,
          storageArea: localStorage
        }));
      }, 100);
      
      navigate('/login');
    } catch (error) {
      console.error('로그아웃 실패:', error);
    }
  };

  const register = async (userData) => {
    try {
      const data = await authService.register(userData);
      
      if (data.success) {
        return { success: true, message: data.message };
      }
      
      return { success: false, error: data.error || '회원가입에 실패했습니다.' };
    } catch (error) {
      console.error('회원가입 실패:', error);
      return { success: false, error: error.error || error.message || '회원가입 중 오류가 발생했습니다.' };
    }
  };

  // [운영사 게임종류 토글] 게임종류 노출 판정 헬퍼.
  //   enabledGameTypes === null(미확정/비로그인) 이면 전부 노출(true)로 간주 → 깜빡임·기존 동작 보존.
  //   [2026-10-07 미니게임] 단, 기본이 미운영인 미니게임은 모를 때 숨긴다(위 GAME_TYPE_DEFAULT_WHEN_UNKNOWN).
  const isGameTypeEnabled = (gameType) => {
    if (!Array.isArray(enabledGameTypes)) return GAME_TYPE_DEFAULT_WHEN_UNKNOWN[gameType] !== false;
    return enabledGameTypes.includes(gameType);
  };

  const value = {
    user,
    loading,
    domainInfo,
    enabledGameTypes,
    isGameTypeEnabled,
    login,
    logout,
    register,
    checkAuthStatus
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// useAuth 훅
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth;